import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Building, Plus, Package, Scale, Calculator, AlertCircle, RotateCcw, RefreshCw
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductUomConversions.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ProductUomConversions = () => {
  const { currentUser, userType, company } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [formData, setFormData] = useState({
    product_id: "",
    from_uom_id: "",
    to_uom_id: "",
    conversion_rate: "",
    company_id: ""
  });

  const [products, setProducts] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingUoms, setLoadingUoms] = useState(false);
  const navigate = useNavigate();

  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };

  const isCompanyAdminUser = () => {
    return userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'manager');
  };

  const userCanCreateConversion = () => {
    return isCompanyAdminUser() || isSystemAdminUser();
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchProducts = async (companyId) => {
    try {
      setLoadingProducts(true);
      const response = await axios.get(`${url}/api/products/dropdown`, {
        params: { company_id: companyId }
      });
      if (response.data.success) {
        setProducts(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchUoms = async (companyId) => {
    try {
      setLoadingUoms(true);
      const response = await axios.get(`${url}/api/uom/dropdown`, {
        params: { company_id: companyId }
      });
      if (response.data.success) {
        setUoms(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load units of measure");
    } finally {
      setLoadingUoms(false);
    }
  };

  useEffect(() => {
    const setup = async () => {
      try {
        if (!userCanCreateConversion()) {
          toast.error("You don't have permission to create UOM conversions");
          navigate("/");
          return;
        }
        if (isSystemAdminUser()) {
          await fetchCompanies();
        } else if (userType === 'company' && company) {
          const companyId = company._id || company;
          setFormData(prev => ({ ...prev, company_id: companyId }));
          await Promise.all([fetchProducts(companyId), fetchUoms(companyId)]);
        }
        setLoading(false);
      } catch (error) {
        toast.error("Failed to load permissions");
        navigate("/");
      }
    };
    setup();
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (duplicateInfo) setDuplicateInfo(null);
    if (type === "number") {
      const numValue = parseFloat(value) || "";
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleCompanyChange = async (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    setFormData(prev => ({ 
      ...prev, 
      company_id: value,
      product_id: "",
      from_uom_id: "",
      to_uom_id: ""
    }));
    setDuplicateInfo(null);
    if (value) {
      await Promise.all([fetchProducts(value), fetchUoms(value)]);
    } else {
      setProducts([]);
      setUoms([]);
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) =>
    setFocused({ ...focused, [field]: formData[field] && (typeof formData[field] === 'string' ? formData[field].length > 0 : true) });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.product_id) newErrors.product_id = "Product is required";
    if (!formData.from_uom_id) newErrors.from_uom_id = "From UOM is required";
    if (!formData.to_uom_id) newErrors.to_uom_id = "To UOM is required";
    if (formData.from_uom_id && formData.to_uom_id && formData.from_uom_id === formData.to_uom_id)
      newErrors.to_uom_id = "From UOM and To UOM must be different";
    if (!formData.conversion_rate) newErrors.conversion_rate = "Conversion rate is required";
    else if (formData.conversion_rate <= 0) newErrors.conversion_rate = "Conversion rate must be greater than 0";
    if (!formData.company_id) newErrors.company_id = "Company selection is required";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }
    try {
      setSubmitting(true);
      setDuplicateInfo(null);
      const response = await axios.post(`${url}/api/puomc`, formData);
      if (response.data.success) {
        toast.success(response.data.reactivated ? "Inactive conversion reactivated successfully!" : "UOM conversion created successfully!");
        setFormData({ product_id: "", from_uom_id: "", to_uom_id: "", conversion_rate: "", company_id: "" });
        setDuplicateInfo(null);
        setTimeout(() => navigate("/ViewProductUomConversions"), 1500);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        const errorMsg = error.response?.data?.message || "This UOM conversion already exists";
        const existingData = error.response?.data?.data;
        setDuplicateInfo({ message: errorMsg, existingData });
        if (existingData?.status === 'inactive') {
          toast.warning(
            <div>
              <p>An inactive conversion exists. Would you like to reactivate?</p>
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                <button style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={() => document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true }))}>
                  <RotateCcw size={14} style={{ marginRight: '5px' }} /> Reactivate with New Rate
                </button>
                <button style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={() => navigate(`/puomc/edit/${existingData.existing_id}`)}>
                  Edit Existing
                </button>
                <button style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  onClick={() => setDuplicateInfo(null)}>
                  Cancel
                </button>
              </div>
            </div>,
            { autoClose: false, closeOnClick: false, draggable: false, closeButton: false }
          );
        } else {
          toast.error(errorMsg);
          if (existingData?.existing_id) {
            toast.info(
              <div>
                <p>Options for active conversion:</p>
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <button style={{ padding: '8px 16px', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    onClick={() => navigate(`/puomc/edit/${existingData.existing_id}`)}>
                    Edit Existing
                  </button>
                  <button style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    onClick={async () => {
                      if (window.confirm("Are you sure you want to delete this active conversion?")) {
                        try {
                          await axios.delete(`${url}/api/puomc/${existingData.existing_id}`);
                          toast.success("Active conversion deleted. Please try creating again.");
                          setDuplicateInfo(null);
                        } catch (err) {
                          toast.error("Failed to delete conversion");
                        }
                      }
                    }}>
                    Delete Active
                  </button>
                </div>
              </div>,
              { autoClose: false }
            );
          }
        }
      } else {
        toast.error(error.response?.data?.message || "Failed to create UOM conversion");
      }
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach(err => { serverErrors[err.field] = err.message; });
        setErrors(prev => ({ ...prev, ...serverErrors }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/ViewProductUomConversions");

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="product-form-container">
          <div className="loading-container">
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="uom-form-container">
        <button className="close-btn" onClick={handleCancel} title="Close">
          <ArrowLeft size={20} />
        </button>

        <div className="form-header">
          <RefreshCw size={24} />
          <h2 className="form-title">Add New Product UOM Conversion</h2>
          <p className="form-subtitle">
            {isSystemAdminUser()
              ? "Create a UOM conversion for any company"
              : `Create a UOM conversion for ${company?.company_name || 'your company'}`
            }
          </p>
        </div>

        {/* Duplicate Alert */}
        {duplicateInfo && (
          <div className={`duplicate-alert ${duplicateInfo.existingData?.status === 'inactive' ? 'warning' : 'error'}`}>
            <AlertCircle size={20} />
            <div className="duplicate-content">
              <strong>{duplicateInfo.existingData?.status === 'inactive' ? 'Inactive Conversion Found' : 'Active Conversion Exists'}</strong>
              <p>{duplicateInfo.message}</p>
              {duplicateInfo.existingData && (
                <div className="existing-info">
                  <div className="info-item">
                    <span className="info-label">ID:</span>
                    <span className="info-value">{duplicateInfo.existingData.existing_id}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className={`status-badge ${duplicateInfo.existingData.status}`}>
                      {duplicateInfo.existingData.status}
                    </span>
                  </div>
                  {duplicateInfo.existingData.conversion_rate && (
                    <div className="info-item">
                      <span className="info-label">Current Rate:</span>
                      <span className="info-value">{duplicateInfo.existingData.conversion_rate}</span>
                    </div>
                  )}
                  {duplicateInfo.existingData.reverse_rate && (
                    <div className="info-item">
                      <span className="info-label">Reverse Rate:</span>
                      <span className="info-value">{duplicateInfo.existingData.reverse_rate?.toFixed(4)}</span>
                    </div>
                  )}
                </div>
              )}
              {duplicateInfo.existingData?.status === 'inactive' && (
                <div className="action-buttons">
                  <button className="reactivate-btn" onClick={() => document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true }))}>
                    <RotateCcw size={14} /> Reactivate with New Rate
                  </button>
                  <button className="edit-btn" onClick={() => navigate(`/puomc/edit/${duplicateInfo.existingData.existing_id}`)}>
                    Edit Existing
                  </button>
                  <button className="clear-btn" onClick={() => setDuplicateInfo(null)}>
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="role-indicator">
          <Building size={20} />
          <div>
            <strong>
              {isSystemAdminUser() ? "System Administrator" : "Company Admin"}
            </strong>
            <div className="role-subtitle">
              {isSystemAdminUser()
                ? "You can create UOM conversions for any company"
                : `You can only create UOM conversions for ${company?.company_name || 'your company'}`
              }
            </div>
          </div>
        </div>

        <form className="product-form" onSubmit={onSubmitHandler}>
          <div className="form-grid">
            {isSystemAdminUser() && (
              <div className={`form-field required ${focused.company_id || formData.company_id ? "filled" : ""} ${errors.company_id ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="company_id"
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("company_id")}
                    onBlur={() => handleBlur("company_id")}
                    required
                    disabled={loadingCompanies}
                  >
                    <option value="">Select Company</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
                <label>Company</label>
                {errors.company_id && <small className="field-error">{errors.company_id}</small>}
                {loadingCompanies && <small className="field-hint">Loading companies...</small>}
              </div>
            )}

            <div className={`form-field required ${focused.product_id || formData.product_id ? "filled" : ""} ${errors.product_id ? 'error' : ''}`}>
              <div className="select-wrapper">
                <select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleChange}
                  onFocus={() => handleFocus("product_id")}
                  onBlur={() => handleBlur("product_id")}
                  required
                  disabled={loadingProducts || products.length === 0}
                >
                  <option value="">Select Product</option>
                  {products.map((product) => (
                    <option key={product._id} value={product._id}>
                      {product.product_name} ({product.product_code})
                    </option>
                  ))}
                </select>
                <Package size={16} className="select-icon" />
              </div>
              <label>Product</label>
              {errors.product_id && <small className="field-error">{errors.product_id}</small>}
              {loadingProducts && <small className="field-hint">Loading products...</small>}
              {!loadingProducts && products.length === 0 && (
                <small className="field-hint error">No products found. Please create products first.</small>
              )}
            </div>

            <div className="form-row two-cols">
              <div className={`form-field required ${focused.from_uom_id || formData.from_uom_id ? "filled" : ""} ${errors.from_uom_id ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="from_uom_id"
                    value={formData.from_uom_id}
                    onChange={handleChange}
                    onFocus={() => handleFocus("from_uom_id")}
                    onBlur={() => handleBlur("from_uom_id")}
                    required
                    disabled={loadingUoms || uoms.length === 0}
                  >
                    <option value="">Select From UOM</option>
                    {uoms.map((uom) => (
                      <option key={uom._id} value={uom._id}>
                        {uom.uom_name} ({uom.uom_type})
                      </option>
                    ))}
                  </select>
                  <Scale size={16} className="select-icon" />
                </div>
                <label>From Unit of Measure</label>
                {errors.from_uom_id && <small className="field-error">{errors.from_uom_id}</small>}
              </div>

              <div className={`form-field required ${focused.to_uom_id || formData.to_uom_id ? "filled" : ""} ${errors.to_uom_id ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="to_uom_id"
                    value={formData.to_uom_id}
                    onChange={handleChange}
                    onFocus={() => handleFocus("to_uom_id")}
                    onBlur={() => handleBlur("to_uom_id")}
                    required
                    disabled={loadingUoms || uoms.length === 0}
                  >
                    <option value="">Select To UOM</option>
                    {uoms.map((uom) => (
                      <option key={uom._id} value={uom._id}>
                        {uom.uom_name} ({uom.uom_type})
                      </option>
                    ))}
                  </select>
                  <Scale size={16} className="select-icon" />
                </div>
                <label>To Unit of Measure</label>
                {errors.to_uom_id && <small className="field-error">{errors.to_uom_id}</small>}
                {loadingUoms && <small className="field-hint">Loading UOMs...</small>}
                {!loadingUoms && uoms.length === 0 && (
                  <small className="field-hint error">No UOMs found. Please create units of measure first.</small>
                )}
              </div>
            </div>

            <div className={`form-field required ${focused.conversion_rate || formData.conversion_rate ? "filled" : ""} ${errors.conversion_rate ? 'error' : ''}`}>
              <input
                type="number"
                name="conversion_rate"
                value={formData.conversion_rate}
                onChange={handleChange}
                onFocus={() => handleFocus("conversion_rate")}
                onBlur={() => handleBlur("conversion_rate")}
                placeholder="1.0"
                step="0.0001"
                min="0.0001"
                required
              />
              <label>Conversion Rate</label>
              {errors.conversion_rate && <small className="field-error">{errors.conversion_rate}</small>}
              <small className="field-hint">Example: 1.5 means 1 From UOM = 1.5 To UOM</small>
            </div>

            {formData.conversion_rate && formData.conversion_rate > 0 && (
              <div className="conversion-preview">
                <div className="preview-item">
                  <Scale size={14} />
                  <span>1 From UOM = {formData.conversion_rate} To UOM</span>
                </div>
                <div className="preview-item">
                  <Calculator size={14} />
                  <span>Or 1 To UOM = {(1 / formData.conversion_rate).toFixed(4)} From UOM</span>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={handleCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={submitting || (duplicateInfo && duplicateInfo.existingData?.status === 'active')}>
              {submitting ? (
                <>
                  <RefreshCw className="animate-spin mr-2" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  {duplicateInfo?.existingData?.status === 'inactive' ? 'Reactivate Conversion' : 'Create Conversion'}
                  <Plus className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductUomConversions;